import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
  Box,
  Flex,
  Text,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  SkeletonText,
  Input,
  InputGroup,
  InputLeftElement,
  CloseButton,
  Button,
} from "@chakra-ui/react";
import {
  constructFullName,
  dateFormat,
  FORMATS,
  getOrganisationMembership,
  getRowIndex,
  renderRecordColor,
  useDebounce,
} from "utils";
import Pagination from "components/Pagination";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, RoleOptions, Roles } from "common";
import { SearchIcon } from "@chakra-ui/icons";
import { useGetProfileQuery } from "redux/services/membership";
import { GetUsersInvitationsRequest } from "redux/services/identity/types";
import { useGetUsersInvitationsQuery } from "redux/services/identity";
import DatePicker from "components/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { clearError } from "redux/slices/errorSlice";
import UserRoleFilter from "components/UserRoleFilter";
import UsersCount from "components/UsersCount";
import DeleteInvitationDialog from "pages/UsersActivationPending/DeleteInvitationDialog";
import { useRoles } from "hooks/useRoles";

const UsersActivationPending: React.FC = () => {
  const error: any = useSelector((state: RootState) => state.error);
  const dispatch = useDispatch();

  const { data: userData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    userData?.data?.memberships
  );
  const orgToken = membershipOrganisation?.token;

  const [searchValue, setSearchValue] = useState<string>();
  const searchValueDebounce = useDebounce(searchValue, 300);

  const [getUsersParamsRequest, setGetUsersParamsRequest] = useState<
    GetUsersInvitationsRequest & { createdAt?: Date }
  >({
    pageNumber: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    orgToken,
  });

  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    data?: any;
  }>({ isOpen: false });

  const { isPSPAdminRole, isBUAdminPSP } = useRoles();

  const {
    data: pendingUsersData,
    isFetching: isFetchingPendingUsers,
    refetch: refetchGetPendingUsers,
  } = useGetUsersInvitationsQuery(
    {
      ...getUsersParamsRequest,
      searchText: getUsersParamsRequest?.searchText ?? undefined,
      createdAt: undefined,
      fromDateTime: getUsersParamsRequest?.createdAt
        ? dayjs(getUsersParamsRequest.createdAt)
          .startOf("day")
          .utc()
          .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
        : undefined,
      toDateTime: getUsersParamsRequest?.createdAt
        ? dayjs(getUsersParamsRequest.createdAt)
          .endOf("day")
          .utc()
          .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
        : undefined,
      userRole: getUsersParamsRequest?.userRole,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: pendingMakersData,
    isFetching: isFetchingPendingMakers,
    refetch: refetchGetPendingMakers
  } =
    useGetUsersInvitationsQuery(
      {
        ...getUsersParamsRequest,
        searchText: getUsersParamsRequest?.searchText ?? undefined,
        createdAt: undefined,
        fromDateTime: getUsersParamsRequest?.createdAt
          ? dayjs(getUsersParamsRequest.createdAt)
            .startOf("day")
            .utc()
            .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
          : undefined,
        toDateTime: getUsersParamsRequest?.createdAt
          ? dayjs(getUsersParamsRequest.createdAt)
            .endOf("day")
            .utc()
            .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
          : undefined,
        userRole: [Roles.BUMaker],
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const pendingMakersRecords = useMemo(
    () => pendingMakersData?.paging.totalRecords ?? 0,
    [pendingMakersData]
  );

  const {
    data: pendingCheckersData,
    isFetching: isFetchingPendingCheckers,
    refetch: refetchGetPendingCheckers
  } =
    useGetUsersInvitationsQuery(
      {
        ...getUsersParamsRequest,
        searchText: getUsersParamsRequest?.searchText ?? undefined,
        createdAt: undefined,
        fromDateTime: getUsersParamsRequest?.createdAt
          ? dayjs(getUsersParamsRequest.createdAt)
            .startOf("day")
            .utc()
            .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
          : undefined,
        toDateTime: getUsersParamsRequest?.createdAt
          ? dayjs(getUsersParamsRequest.createdAt)
            .endOf("day")
            .utc()
            .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
          : undefined,
        userRole: [Roles.BUChecker],
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const pendingCheckersRecords = useMemo(
    () => pendingCheckersData?.paging.totalRecords ?? 0,
    [pendingCheckersData]
  );

  const {
    data: pendingApproversData,
    isFetching: isFetchingPendingApprovers,
    refetch: refetchGetPendingApprovers
  } =
    useGetUsersInvitationsQuery(
      {
        ...getUsersParamsRequest,
        searchText: getUsersParamsRequest?.searchText ?? undefined,
        createdAt: undefined,
        fromDateTime: getUsersParamsRequest?.createdAt
          ? dayjs(getUsersParamsRequest.createdAt)
            .startOf("day")
            .utc()
            .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
          : undefined,
        toDateTime: getUsersParamsRequest?.createdAt
          ? dayjs(getUsersParamsRequest.createdAt)
            .endOf("day")
            .utc()
            .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
          : undefined,
        userRole: [Roles.BUApprover],
      },
      {
        refetchOnMountOrArgChange: true,
      }
    );

  const pendingApproversRecords = useMemo(
    () => pendingApproversData?.paging.totalRecords ?? 0,
    [pendingApproversData]
  );

  const userRolesRecords = useMemo(
    () => [
      {
        value: Roles.BUMaker,
        records: pendingMakersRecords,
        textColor: renderRecordColor(pendingMakersRecords),
      },
      {
        value: Roles.BUChecker,
        records: pendingCheckersRecords,
        textColor: renderRecordColor(pendingCheckersRecords),
      },
      {
        value: Roles.BUApprover,
        records: pendingApproversRecords,
        textColor: renderRecordColor(pendingApproversRecords),
      },
    ],
    [pendingApproversRecords, pendingCheckersRecords, pendingMakersRecords]
  );

  const isLoading = useMemo(
    () =>
      isFetchingPendingApprovers ||
      isFetchingPendingCheckers ||
      isFetchingPendingMakers ||
      isFetchingPendingUsers,
    [
      isFetchingPendingApprovers,
      isFetchingPendingCheckers,
      isFetchingPendingMakers,
      isFetchingPendingUsers,
    ]
  );

  const refetchAllUsers = () => {
    if (isPSPAdminRole) {
      return Promise.all([
        refetchGetPendingUsers(),
        refetchGetPendingMakers(),
        refetchGetPendingCheckers(),
      ]);
    }

    if (isBUAdminPSP) {
      return Promise.all([
        refetchGetPendingUsers(),
        refetchGetPendingApprovers(),
      ]);
    }

    return Promise.all([
      refetchGetPendingUsers(),
      refetchGetPendingMakers(),
      refetchGetPendingCheckers(),
      refetchGetPendingApprovers(),
    ]);
  }

  const handleFilterUserRole = (option: any) => {
    setGetUsersParamsRequest((prev) => ({
      ...prev,
      pageNumber: DEFAULT_PAGE,
      userRole: option.label === "User Role" ? undefined : [option.value],
    }));
  };

  useEffect(() => {
    if (typeof searchValueDebounce === "string") {
      setGetUsersParamsRequest({
        ...getUsersParamsRequest,
        pageNumber: DEFAULT_PAGE,
        searchText: searchValueDebounce,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueDebounce]);

  useEffect(() => {
    return () => {
      if (error.type === "registerUserError") {
        dispatch(clearError());
      }
    };
  }, [dispatch, error, error.type]);

  return (
    <Flex
      className="UsersActivationPending"
      w="100%"
      h="100%"
      p="20px"
      flexDir={"column"}
      position="relative"
      minHeight="calc(100vh - 124px)"
    >
      {/* <HelmetHead title="DigiBank - Payment" /> */}
      <Flex w="100%" justifyContent="space-between">
        <Text textStyle={"h1"}>View Activation Pending Enterprise Users</Text>
        {!isLoading && (
          <UsersCount
            makerRecords={pendingMakersRecords}
            checkerRecords={pendingCheckersRecords}
            approverRecords={pendingApproversRecords}
            title="Pending Users Counts"
          />
        )}
      </Flex>

      <Flex gap="20px" wrap="wrap" my="20px" align="center">
        <Box>
          <InputGroup>
            <InputLeftElement
              pointerEvents="none"
              children={<SearchIcon color="#353333" />}
            />
            <Input
              data-testid="input-search"
              placeholder="Search by name"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
            />
          </InputGroup>
        </Box>

        <UserRoleFilter
          handleFilterUserRole={handleFilterUserRole}
          userRolesRecords={userRolesRecords}
        />

        <Box>
          <DatePicker
            allowClear
            selected={getUsersParamsRequest?.createdAt}
            onChange={(e) => {
              setGetUsersParamsRequest({
                ...getUsersParamsRequest,
                pageNumber: DEFAULT_PAGE,
                createdAt: e,
              });
            }}
            placeholder="Created date"
          />
        </Box>
      </Flex>

      {error.type === "registerUserError" && (
        <Box
          width="100%"
          backgroundColor="orange.200"
          p="15px 20px"
          borderRadius="5px"
          position="relative"
          paddingRight="50px"
        >
          <Text
            fontSize="12px"
            color="black"
            whiteSpace="pre-line"
            lineHeight={1.5}
          >
            {error.message}
          </Text>
          <CloseButton
            onClick={() => dispatch(clearError())}
            position="absolute"
            right="5px"
            top="5px"
            size="sm"
            color="blackAlpha.700"
          />
        </Box>
      )}

      {isLoading ? (
        <SkeletonText
          data-testid="skeleton-text"
          noOfLines={15}
          spacing="10px"
          p="10px 18px"
        />
      ) : (
        <>
          <TableContainer mt="20px">
            <Table
              data-testid="table-container"
              sx={{
                tableLayout: "fixed",
                tbody: {
                  td: { wordBreak: "break-all", whiteSpace: "pre-line" },
                },
              }}
            >
              <Thead>
                <Tr>
                  <Th isNumeric w="80px">
                    No.
                  </Th>
                  <Th w="300px">Name</Th>
                  <Th w="300px">Email</Th>
                  <Th w="200px">User Role</Th>
                  <Th w="200px">Created At</Th>
                  <Th w="135px">Action</Th>
                </Tr>
              </Thead>

              <Tbody>
                {pendingUsersData?.data?.map((item, index) => {
                  const userRole = RoleOptions.find((option) =>
                    item?.roles?.includes(option.value)
                  );

                  const roleName = isPSPAdminRole ? userRole?.data?.title.replace("Business", "PSP") : userRole?.data?.title ?? "-";

                  return (
                    <Tr key={item.emailAddress}>
                      <Td isNumeric>
                        {getRowIndex(
                          getUsersParamsRequest.pageNumber,
                          getUsersParamsRequest.pageSize,
                          index
                        )}
                      </Td>

                      <Td>
                        {constructFullName(
                          item.firstName,
                          undefined,
                          item.lastName
                        ) || "-"}
                      </Td>
                      <Td>{item.emailAddress}</Td>
                      <Td>{roleName}</Td>
                      <Td>
                        {dateFormat(
                          item.createAt,
                          true,
                          FORMATS["YYYY-MM-DD HH:mm"]
                        )}
                      </Td>
                      <Td>
                        <Button
                          data-testid="action-btn"
                          fontSize="14px"
                          colorScheme="blue"
                          onClick={() =>
                            setDialogState({ isOpen: true, data: item })
                          }
                        >
                          Delete
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>

          {pendingUsersData?.data?.length === 0 && (
            <Text p="20px" textAlign="center" fontWeight="bold">
              No records found
            </Text>
          )}
        </>
      )}

      <Pagination
        totalRecords={pendingUsersData?.paging?.totalRecords ?? 0}
        pageSize={getUsersParamsRequest.pageSize}
        onChangePage={(pageNumber) =>
          setGetUsersParamsRequest({ ...getUsersParamsRequest, pageNumber })
        }
      />

      <DeleteInvitationDialog
        {...dialogState}
        onClose={() => setDialogState({ isOpen: false })}
        onSuccess={async () => {
          await refetchAllUsers();
        }}
      />
    </Flex>
  );
};

export default UsersActivationPending;
